import { MoreVert } from '@mui/icons-material';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import { Chip, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import PaiPagination from 'features/ui/base-components/pai-pagination';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import { t } from 'i18next';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { User } from '../account-management.types';
import UserItemMenu from './user-item-menu';
import { useUserData } from 'hooks/use-user-data';
import { Roles } from 'enums';
import PaiLinkAlt from 'features/ui/base-components/pai-link-alt';
import { formatDateTime } from 'utils/date.utils';

interface TenantUserItemProps {
  isAdmin: boolean;
  user: User;

  handleOpenEditUserDialog(): void;

  handleOpenDeleteUserDialog(): void;

  handleOpenAdminSudoLoginDialog(): void;

  handleOpenManageUserLicensesDialog(): void;

  handleResendInvitation(): void;

  resendInvitationLoading: boolean;
}

const TenantUserItem: React.FC<TenantUserItemProps> = ({
  isAdmin,
  user,
  handleOpenEditUserDialog,
  handleOpenDeleteUserDialog,
  handleOpenAdminSudoLoginDialog,
  handleOpenManageUserLicensesDialog,
  handleResendInvitation,
  resendInvitationLoading,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<any | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleOpenMenu = (el: any) => {
    setMenuOpen(true);
    setAnchorEl(el);
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
    setAnchorEl(null);
  };
  const canResendInvitation = user.invitationStatus === 'pending' || user.invitationStatus === 'expired';

  return (
    <>
      <TableRow>
        <TableCell>
          <PaiLink
            text={`${user.firstname ? user.firstname : ''} ${user.lastname ? user.lastname : ''}`}
            size={'md'}
            variant={'primary'}
            underline={'hover'}
            IconComponent={<LoginRoundedIcon />}
            iconPlacement={'end'}
            action={handleOpenAdminSudoLoginDialog}
          />
        </TableCell>
        <TableCell>{user.email}</TableCell>
        {isAdmin && <TableCell>{formatDateTime(new Date(user.createdAt).toISOString(), 'Pp')}</TableCell>}
        <TableCell>{t(user.role)}</TableCell>
        {isAdmin && (
          <TableCell>
            <Chip className={user.isActive ? classes.activeChip : classes.inactiveChip} label={user.isActive ? t('active') : t('inactive')} size={'small'} />
          </TableCell>
        )}
        {isAdmin && (
          <TableCell>
            {user.invitationStatus && user.invitationStatus !== 'none' ? (
              <Stack direction={'row'} alignItems={'center'} gap={1}>
                <Chip
                  className={classes.invitationChip}
                  color={user.invitationStatus === 'expired' ? 'error' : user.invitationStatus === 'pending' ? 'warning' : 'success'}
                  label={t(`invitationStatus_${user.invitationStatus}`)}
                  size={'small'}
                />
                {canResendInvitation ? (
                  <PaiButton
                    text={t('resendInvitationShort')}
                    size={'small'}
                    border={'rounded'}
                    action={handleResendInvitation}
                    loading={resendInvitationLoading}
                  />
                ) : null}
              </Stack>
            ) : (
              '-'
            )}
          </TableCell>
        )}
        {isAdmin && (
          <TableCell>
            <PaiLinkAlt underline={'hover'} onClick={handleOpenManageUserLicensesDialog} sx={(theme) => ({ fontSize: theme.typography.pxToRem(14) })}>
              {user.licenses?.length}
            </PaiLinkAlt>
          </TableCell>
        )}
        <TableCell>
          <IconButton
            className={classes.menuIconButton}
            size={'small'}
            onClick={(event: React.MouseEvent<HTMLElement>) => handleOpenMenu(event.currentTarget)}
          >
            <MoreVert />
          </IconButton>
        </TableCell>
      </TableRow>

      <UserItemMenu
        anchorEl={anchorEl}
        menuOpen={menuOpen}
        handleAdminLogin={handleOpenAdminSudoLoginDialog}
        handleOpenEditUserDialog={handleOpenEditUserDialog}
        handleOpenDeleteUserDialog={handleOpenDeleteUserDialog}
        handleCloseMenu={handleCloseMenu}
      />
    </>
  );
};

interface Props {
  tenantUsers: User[];
  tenantUsersTotal: number;
  tenantUsersItemsPerPage: number;
  tenantUsersPage: number;
  handleOpenCreateUserDialog(): void;
  handleOpenEditUserDialog(user: User): () => void;
  handleOpenDeleteUserDialog(user: User): () => void;
  handleOpenAdminSudoLoginDialog(user: User): () => void;
  tenantUsersHandleChangePage(event: unknown, newPage: number): void;
  handleOpenManageUserLicensesDialog(user: User): () => void;
  handleResendInvitation(user: User): () => void;
  resendingInvitationUserId: string | null;
}

const TenantUsersTable: React.FC<Props> = ({
  tenantUsers,
  tenantUsersTotal,
  tenantUsersItemsPerPage,
  tenantUsersPage,
  handleOpenCreateUserDialog,
  handleOpenEditUserDialog,
  handleOpenDeleteUserDialog,
  handleOpenAdminSudoLoginDialog,
  tenantUsersHandleChangePage,
  handleOpenManageUserLicensesDialog,
  handleResendInvitation,
  resendingInvitationUserId,
}) => {
  const { classes } = useStyles();
  const { role } = useUserData();

  const isAdmin = role === Roles.ADMIN || role === Roles.OWNER;

  return (
    <Stack gap={2}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: isAdmin ? '25%' : '33%' }}>{t('name')}</TableCell>
            <TableCell sx={{ width: isAdmin ? '20%' : '33%' }}>{t('email')}</TableCell>
            {isAdmin && <TableCell sx={{ width: '16%' }}>{t('createDate')}</TableCell>}
            <TableCell sx={{ width: isAdmin ? '10%' : '33%' }}>{t('role')}</TableCell>
            {isAdmin && <TableCell sx={{ width: '10%' }}>{t('status')}</TableCell>}
            {isAdmin && <TableCell sx={{ width: '18%' }}>{t('invitation')}</TableCell>}
            {isAdmin && <TableCell sx={{ width: '8%' }}>{t('licenses')}</TableCell>}
            <TableCell sx={{ width: '28px' }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {!_.isEmpty(tenantUsers) ? (
            tenantUsers.map((user) => (
              <TenantUserItem
                key={user.id}
                isAdmin={isAdmin}
                user={user}
                handleOpenEditUserDialog={handleOpenEditUserDialog(user)}
                handleOpenDeleteUserDialog={handleOpenDeleteUserDialog(user)}
                handleOpenAdminSudoLoginDialog={handleOpenAdminSudoLoginDialog(user)}
                handleOpenManageUserLicensesDialog={handleOpenManageUserLicensesDialog(user)}
                handleResendInvitation={handleResendInvitation(user)}
                resendInvitationLoading={resendingInvitationUserId === (user.globalUser || user.id)}
              />
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={isAdmin ? 7 : 4} style={{ textAlign: 'center' }}>
                {t('noUsersFound')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Stack direction={'row'} justifyContent={'space-between'} gap={2}>
        <PaiButton text={t('addUser')} size={'small'} border={'rounded'} action={handleOpenCreateUserDialog} />
        <PaiPagination
          total={tenantUsersTotal}
          itemsPerPage={tenantUsersItemsPerPage}
          page={tenantUsersPage}
          handleChangePage={tenantUsersHandleChangePage}
          hidden={tenantUsersTotal <= tenantUsersItemsPerPage}
        />
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  table: {
    border: `1px solid ${theme.palette.divider}`,
    '& th': {
      border: '1px solid #ddd',
      padding: '4px 24px',
      fontSize: theme.typography.pxToRem(13),
      background: theme.palette.greys.lightest,
      fontWeight: 'bold',
    },
    '& td, th': {
      border: '1px solid #ddd',
      padding: '8px 24px',
      textAlign: 'left',
      verticalAlign: 'middle',
      fontSize: theme.typography.pxToRem(13),
    },
  },
  menuIconButton: {
    padding: '4px',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(16),
      color: theme.palette.text.secondary,
    },
  },
  activeChip: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  inactiveChip: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
  invitationChip: {
    minWidth: '80px',
  },
}));

export default TenantUsersTable;
